/* eslint-disable react/require-default-props */
import React, {
  FC,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import Model from "@tripian/model";
import {
  AccommondationCard,
  DirectionInfo,
  RouteResult,
  SvgIcons,
  CustomSlider,
} from "@tripian/react";
import ICombinedState from "../../../../redux/model/ICombinedState";
import useTrip from "../../../../hooks/useTrip";
import useStep from "../../../../hooks/useStep";
import useReaction from "../../../../hooks/useReaction";
import useAlternative from "../../../../hooks/useAlternative";
import useFocus from "../../../../hooks/useFocus";
import useTranslate from "../../../../hooks/useTranslate";
import SortableStepList from "../../../../components/SortableStepList/SortableStepList";
import StepTimeframe from "../../../../components/StepTimeframe/StepTimeframe";
import classes from "./Itineraries.module.scss";

const getAccommendationStep = (
  accommendation: Model.Accommodation
): Model.Step => {
  const accommendationPoi: Model.Poi = {
    id: "",
    icon: "Homebase",
    name: accommendation.name || "AccommendationState Name",
    description: accommendation.address || "AccommendationState Address",
    coordinate: accommendation.coordinate || { lat: 0, lng: 0 },
    web: accommendation.refID ?? "",
    address: accommendation.provider ?? "Google",
    image: {
      url: accommendation.imageUrl ?? "",
      imageOwner: { title: "", url: "" /* avatar: "" */ },
      width: null,
      height: null,
    },
    cityId: 0,
    gallery: [],
    price: 5,
    rating: 5,
    ratingCount: 0,
    phone: null,
    bookings: [],
    category: [],
    tags: [],
    mustTries: [],
    cuisines: null,
    attention: null,
    hours: null,
    closed: [],
    distance: null,
    status: true,
    safety: [],
    offers: [],
  };

  const accommendationStep: Model.Step = {
    id: 0,
    poi: accommendationPoi,
    alternatives: [],
    order: -1,
    score: null,
    scoreDetails: [],
    times: { from: "", to: "" },
    warningMessage: [],
    stepType: "poi",
  };

  return accommendationStep;
};

interface IItineraries {
  plans?: Model.Plan[];
  planDayIndex: number;
  plansIdDate: {
    id: number;
    date: string;
  }[];
  plansIdDateWithTimeframe: {
    id: number;
    date: string;
    startTime: string;
    endTime: string;
  }[];
  planUpdateOrders: (planId: number, orders: number[]) => Promise<Model.Plan[]>;
  planUpdateTime: (
    planId: number,
    minDayIndex: number,
    startTime: string,
    endTime: string
  ) => Promise<Model.Plan[]>;
  changePlanDayIndex: (newPlanDayIndex: number) => void;
  alternativesStepId?: number;
  setAlternativesStepId: (stepId?: number) => void;
  customPoiModalShow: () => void;
  // selectedPoiCategoryIds: number[];
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  setShowCloneModal: (show: boolean) => void;
  onBookingClick: () => void;
}

const Itineraries: FC<IItineraries> = ({
  plans,
  planDayIndex,
  plansIdDate,
  plansIdDateWithTimeframe,
  planUpdateOrders,
  planUpdateTime,
  changePlanDayIndex,
  alternativesStepId,
  setAlternativesStepId,
  customPoiModalShow,
  // selectedPoiCategoryIds,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  setShowCloneModal,
  onBookingClick,
}) => {
  const [legsOffset, setLegsOffset] = useState<number>(0);
  const { tripReference } = useTrip();
  const scrollRef = useRef<HTMLDivElement>(null); // Use ref to track the element
  const { stepReplace, stepUpdateTimes, stepDelete } = useStep();
  const { alternatives } = useAlternative();
  const { reactions, reactionAdd, reactionDelete, loadingReactionStep } =
    useReaction();
  const { focusStep, focusAlternative, focusLost } = useFocus();

  const { t } = useTranslate();

  const plan = useMemo(
    () => (plans ? plans[planDayIndex] : undefined),
    [planDayIndex, plans]
  );

  const { legs, readOnlyTrip } = useSelector((state: ICombinedState) => ({
    legs: state.gmap.legs,
    readOnlyTrip: state.trip.readOnly,
    tripHash: state.trip?.reference?.tripHash,
    shared: state.trip.reference?.shared,
  }));

  const { hashParam } = useParams<{ hashParam: string; dayIndex: string }>();

  const sharedTrip = useMemo(() => {
    if (!hashParam) return false;
    const params = hashParam.split("!");
    return params.length > 1 && hashParam.split("!")[1] === "s";
  }, [hashParam]);

  useEffect(() => {
    if (tripReference?.tripProfile.accommodation) setLegsOffset(1);
    else setLegsOffset(0);
  }, [tripReference]);

  const memoizedSortedSteps = useMemo(() => {
    return plan?.steps.sort((a, b) => a.order - b.order) || [];
  }, [plan?.steps]);

  // step current reactions
  const memoizedThumbs = useCallback(
    (step?: Model.Step) => {
      let t = 0;
      const stepReaction = reactions?.find(
        userReaction =>
          userReaction.stepId === step?.id && userReaction.poiId === step.poi.id
      );
      if (stepReaction) {
        if (stepReaction.reaction === Model.REACTION.THUMBS_UP) {
          t = 1;
        } else if (stepReaction.reaction === Model.REACTION.THUMBS_DOWN) {
          t = -1;
        } else {
          // eslint-disable-next-line no-console
          console.warn("unknown step reaction", stepReaction.reaction);
        }
      } else {
        // console.log('no step reaction', step.id);
      }
      return t;
    },
    [reactions]
  );

  const memoizedThumbsClicked = useCallback(
    (step: Model.Step, thumbs: number) => {
      const userReactionRequest: Model.UserReactionRequest = {
        stepId: step.id,
        poiId: step.poi.id,
        reaction: Model.REACTION.THUMBS_UP,
      };
      if (thumbs === -1)
        userReactionRequest.reaction = Model.REACTION.THUMBS_DOWN;
      reactionAdd(userReactionRequest);
    },
    [reactionAdd]
  );

  const memoizedThumbsUndo = useCallback(
    (step: Model.Step) => {
      const stepUserReaction = reactions?.find(
        userReaction => userReaction.stepId === step.id
      );
      if (stepUserReaction) {
        reactionDelete(stepUserReaction.id, stepUserReaction.stepId || 0);
      } else {
        // eslint-disable-next-line no-console
        console.error(`StepCard.ThumbsUndo called with =${step.id}`);
      }
    },
    [reactions, reactionDelete]
  );

  const emptyPlanMessage = () => {
    let message = t("trips.myTrips.itinerary.error.emptyMesssage");

    if (plans && plan) {
      if (planDayIndex === 0 && plan.generatedStatus === -1) {
        message = t("trips.myTrips.itinerary.error.generatedStatusNegative1");
      } else if (
        planDayIndex === plans.length - 1 &&
        plans[plans.length - 1].generatedStatus === -1
      ) {
        message = t("trips.myTrips.itinerary.error.generatedStatusNegative1");
      }
    }

    return message;
  };

  const alternativeDayNumbers = (alternativePois: Model.Poi[]): number[][] => {
    const getDayNumbers = (poiId: string): number[] => {
      if (poiId === "") return [];

      const partOfDayNumbers: number[] = [];
      if (plans) {
        for (let i = 0; i < plans.length; i += 1) {
          const stepIndex = plans[i].steps.findIndex(
            step => step.poi.id === poiId
          );
          if (stepIndex > -1) {
            partOfDayNumbers.push(i + 1);
          }
        }
        return partOfDayNumbers;
      }
      return [];
    };

    return alternativePois.map(ap => getDayNumbers(ap.id));
  };

    const windowOpenBookARide = (leg: RouteResult.ILeg) => {
        const travel = leg.travel?.between;

        if (!travel?.pickup?.coordinate || !travel?.dropoff?.coordinate) {
            window.open("https://m.uber.com", "_blank");
            return;
        }

        const pickup = {
            latitude: travel.pickup.coordinate.lat,
            longitude: travel.pickup.coordinate.lng,
            addressLine1: travel.pickup.nickname,
            addressLine2: travel.pickup.formatted_address
        };

        const dropoff = {
            latitude: travel.dropoff.coordinate.lat,
            longitude: travel.dropoff.coordinate.lng,
            addressLine1: travel.dropoff.nickname,
            addressLine2: travel.dropoff.formatted_address
        };

        const uberClientId =
            window.tconfig.PROVIDERS.transportation.find(
                x => x.id === Model.PROVIDER_ID.UBER
            )?.clientId ?? "";

        const bookARideUrl =
            "https://m.uber.com/looking" +
            `?client_id=${uberClientId}` +
            `&pickup=${encodeURIComponent(JSON.stringify(pickup))}` +
            `&drop[0]=${encodeURIComponent(JSON.stringify(dropoff))}`;

        window.open(bookARideUrl, "_blank");
    };


    const smoothScroll = useCallback(() => {
    const timer = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const sortableStepList =
    plan?.steps.length === 0 || plan?.steps === undefined ? (
      <div className="m10">{emptyPlanMessage()}</div>
    ) : (
      <>
        {tripReference?.tripProfile.accommodation && legs[0] ? (
          <div className="">
            <DirectionInfo
              distance={legs[0].distance.text}
              direction={legs[0].duration.text}
              travel={legs[0].travel}
              bookaride={() => {
                windowOpenBookARide(legs[0]);
              }}
              hideBookaRide={!window.tconfig.SHOW_BOOK_A_RIDE}
              t={t}
            />
          </div>
        ) : null}
        <SortableStepList
          steps={memoizedSortedSteps}
          callbackSortableStepList={list => {
            if (readOnlyTrip) cloneTrip();
            else if (plan) planUpdateOrders(plan.id, list);
          }}
          alternatives={alternatives ?? []}
          alternativeDayNumbers={alternativeDayNumbers}
          alternativesStepId={alternativesStepId}
          showAlternativesChange={(stepId, show) => {
            setAlternativesStepId(show ? stepId : undefined);
          }}
          // showRemoveReplaceButtons={!(sharedTrip && window.tconfig.WIDGET_THEME_1)}
          hideScore={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideStepsTime={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideFeatures={sharedTrip && window.tconfig.WIDGET_THEME_1}
          hideCuisine={sharedTrip && window.tconfig.WIDGET_THEME_1}
          isWidget={sharedTrip && window.tconfig.WIDGET_THEME_1}
          alternativePoiCardClicked={(stepId, alternativePoi) => {
            focusAlternative(stepId, alternativePoi);
          }}
          alternativeReplace={(stepId, alternativePoi) => {
            if (readOnlyTrip) cloneTrip();
            else {
              focusLost();
              stepReplace(stepId, alternativePoi.id);
            }
          }}
          memoizedThumbs={memoizedThumbs}
          thumbsClicked={(step, like) => {
            if (readOnlyTrip) cloneTrip();
            else {
              memoizedThumbsClicked(step, like);
            }
          }}
          timesClicked={(stepId, from, to) => {
            if (readOnlyTrip) cloneTrip();
            else {
              focusLost();
              stepUpdateTimes(stepId, from, to);
            }
          }}
          loadingReactionStep={loadingReactionStep}
          userReactionUndo={step => {
            if (readOnlyTrip) cloneTrip();
            else {
              memoizedThumbsUndo(step);
              setAlternativesStepId(undefined);
            }
          }}
          userReactionRemoveStep={stepId => {
            if (readOnlyTrip) cloneTrip();
            else {
              stepDelete(stepId);
            }
          }}
          focusStep={(step: Model.Step) => {
            focusStep(step);
          }}
          onBookClick={(step: Model.Step) => {
            focusStep(step);
            onBookingClick();
          }}
          legs={legs}
          legsOffset={legsOffset}
          bookaride={() => windowOpenBookARide(legs[0])}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          toursLoading={toursLoading}
          t={t}
          /* readOnlyTrip={readOnlyTrip} */
        />
      </>
    );

  const accommendationMemo = useMemo(() => {
    if (tripReference?.tripProfile.accommodation) {
      const { accommodation } = tripReference.tripProfile;

      return (
        <AccommondationCard
          accommodation={accommodation}
          clicked={() => {
            const accommendationStep = getAccommendationStep(accommodation);
            focusStep(accommendationStep);
          }}
        />
      );
    }
    return null;
  }, [tripReference, focusStep]);

  const cloneTrip = () => {
    setShowCloneModal(true);
  };

  return (
    <div className="h-full py-4">
      <div ref={scrollRef} className="w-full relative">
        <div ref={scrollRef}>
          {plansIdDate.length > 0 &&
          !(sharedTrip && window.tconfig.WIDGET_THEME_1) ? (
            <div className="py-1">
              <CustomSlider scrollSize={150}>
                {plansIdDate.map((planIdDate, index) => {
                  return (
                    <div
                      key={index}
                      className={`text-sm !w-auto rounded-3xl border border-solid border-gray-300 my-0 py-[0.4375rem] px-4 cursor-pointer min-w-fit font-medium whitespace-nowrap ${
                        planDayIndex === index
                          ? "text-white bg-primary-color"
                          : "text-black bg-white"
                      }`}
                      onClick={() => {
                        changePlanDayIndex(index);
                        smoothScroll();
                      }}
                    >
                      <span
                        className={`${planDayIndex === index ? "font-bold" : "font-medium"}`}
                      >
                        {t("trips.myTrips.itinerary.day")}{" "}
                        <span>
                          {plansIdDate.findIndex(o => o.id === planIdDate.id) +
                            1}
                        </span>
                      </span>
                      <span>
                        {planDayIndex === index && (
                          <>
                            <span> &nbsp;|&nbsp; </span>
                            <span>
                              {moment(planIdDate.date).format("MMM DD")}
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
              </CustomSlider>
            </div>
          ) : null}
        </div>

        <div className="my-4">
          <div className="font-medium text-xl text-[#0B1215]">
            {t("trips.myTrips.itinerary.title")}
          </div>
        </div>

        {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && (
          <div className="my-4 flex items-center justify-between">
            <StepTimeframe
              dayIndex={planDayIndex}
              plansIdDateWithTimeframe={plansIdDateWithTimeframe}
              defaultTimeframe={{
                startTime: plan?.startTime || "09:00",
                endTime: plan?.endTime || "21:00",
              }}
              callbackStepTimeframe={(timeframe, minDayIndex, planId) => {
                planUpdateTime(
                  planId,
                  minDayIndex,
                  timeframe.startTime,
                  timeframe.endTime
                );
              }}
            />
            <div
              className="border border-solid border-neutral-300 rounded-full flex items-center justify-center w-10 h-10 cursor-pointer bg-primary-color"
              onClick={() => customPoiModalShow()}
            >
              <SvgIcons.Add
                size="1.25rem"
                fill="#fff"
                className="primary-color"
              />
            </div>
          </div>
        )}
        <div className={`${classes.stepList}`}>
          {plan?.generatedStatus === 2 && (
            <div className="ms-7 my-4 text-sm text-yellow-400">
              {t("trips.myTrips.itinerary.error.generatedStatus2")}
            </div>
          )}
          {plan?.generatedStatus === 3 && (
            <div className="ms-7 my-4 text-sm text-yellow-400">
              {t("trips.myTrips.itinerary.error.generatedStatus3")}
            </div>
          )}
          {!(sharedTrip && window.tconfig.WIDGET_THEME_1) && accommendationMemo}
          {sortableStepList}
        </div>
      </div>
    </div>
  );
};

export default Itineraries;
